import React from "react";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../components/TitleWithDescription/TitleWithDescription";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import Title from "../../components/Title/Title";
import { isMobile, isMobileOrTablet } from "../../utils/appUtils";
import { useLanguage } from "../../context/LanguageContext";
import { getBiggestGemstoneTranslations, BiggestGemstoneTranslations } from "../../translations/biggestGemstone";
import { useEditMode } from "../../context/EditModeContext";
import EditableText from "../../components/EditableText/EditableText";
import { usePageContent } from "../../hooks/usePageContent";

function TheBiggestGemstone(): JSX.Element {
  const { language } = useLanguage();
  const { isEditMode } = useEditMode();
  const { content, loading } = usePageContent<BiggestGemstoneTranslations>('biggest-gemstone');

  // Use translations as fallback content
  const t = content || getBiggestGemstoneTranslations(language);

  // Show loading state if needed
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Background cover at the top */}
      <BackgroundCover
        backgroundHeight={isMobileOrTablet() ? (isMobile() ? 400 : 600) : undefined}
        imagePosition={isMobileOrTablet() ? (isMobile() ? "center" : "initial") : "initial"}
        backgroundImage="/images/biggest_gemstone_background.jpg"
        isWatchPage={true}
      >
        <div className="absolute top-1/2 left-[10%] md:left-[100px] -translate-y-1/2 z-10 hidden md:block">
          <Title
            firstLine={isEditMode ? (
              <EditableText
                contentKey="biggest-gemstone.backgroundTitle.firstLine"
                defaultValue={t.backgroundTitle.firstLine}
              />
            ) : t.backgroundTitle.firstLine}
            secondLine={isEditMode ? (
              <EditableText
                contentKey="biggest-gemstone.backgroundTitle.secondLine"
                defaultValue={t.backgroundTitle.secondLine}
              />
            ) : t.backgroundTitle.secondLine}
            className="text-white"
            animate={true}
          />
        </div>
      </BackgroundCover>

      {/* Introduction */}
      <div className="w-full px-4 md:px-0 py-8 md:py-16">
        <TitleWithDescription
          title={{
            firstLine: isEditMode ? (
              <EditableText
                contentKey="biggest-gemstone.intro.title.firstLine"
                defaultValue={t.intro.title.firstLine}
              />
            ) : t.intro.title.firstLine,
            secondLine: isEditMode ? (
              <EditableText
                contentKey="biggest-gemstone.intro.title.secondLine"
                defaultValue={t.intro.title.secondLine}
              />
            ) : t.intro.title.secondLine,
          }}
          description={isEditMode ? (
            <EditableText
              contentKey="biggest-gemstone.intro.description"
              defaultValue={t.intro.description}
              multiline={true}
            />
          ) : t.intro.description}
          layout="row"
          className="px-4 md:px-8 lg:px-24"
        />
      </div>

      <div className="w-full">
        <div className="w-full bg-gradient-to-b from-[#E1D8D3] via-[#FFFFFF] to-[#E1D8D3] py-8 md:py-12">
          <div className="mx-auto px-4 md:px-12 space-y-8 md:space-y-12">
            {t.timeline.map((section, index) => (
              <TimelineSection
                key={index}
                title={{
                  firstLine: isEditMode ? (
                    <EditableText
                      contentKey={`biggest-gemstone.timeline.${index}.title.firstLine`}
                      defaultValue={section.title.firstLine}
                    />
                  ) : section.title.firstLine,
                  secondLine: isEditMode ? (
                    <EditableText
                      contentKey={`biggest-gemstone.timeline.${index}.title.secondLine`}
                      defaultValue={section.title.secondLine}
                    />
                  ) : section.title.secondLine,
                }}
                description={isEditMode ? (
                  <EditableText
                    contentKey={`biggest-gemstone.timeline.${index}.description`}
                    defaultValue={section.description}
                    multiline={true}
                  />
                ) : section.description}
                mediaPosition={index % 2 === 0 ? "right" : "left"}
                media={{
                  type: "image",
                  src: `/images/secret_rainbow_sub${index + 2}.jpg`,
                  alt: `Section ${index + 1}`,
                }}
                showLine={index < t.timeline.length - 1}
              />
            ))}

            {/* Video Section */}
            <div className="w-full aspect-video max-w-4xl mx-auto">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/QNkrAsjYT7M?si=EcdBmlnzCyoq3weF"
                title="The Secret Rainbow Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TheBiggestGemstone;
